import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';

import Button from 'components/form/Button';
import UploadInput from 'components/form/UploadInput';
import Modal from 'components/Layout/Modal';
import { useDispatch } from 'react-redux';
import { objectToFormData } from 'utils/helpers';
import { handleUploadFullPaper, throwSuccess } from 'store/slices';
import { useParams } from 'react-router-dom';
import { icons } from 'utils/constants';
import TextArea from 'components/form/TextArea';

const FullPaperAddPopup = ({ type, getAbstractFullPaper }) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const isReUpload = type === 'ReUpload';
  const params = useParams();
  const { paperId } = params;

  const validationSchema = Yup.object().shape({
    abstract_path: Yup.mixed().required('Please upload a full paper'),
  });

  const handleClose = () => {
    setShowModal(false);
  };

  const handleApiSubmit = async (values) => {
    setBtnLoading(true);
    const payload = objectToFormData({
      abstract_id: paperId,
      full_paper: values?.abstract_path,
      is_resubmited: isReUpload ? '1' : '0',
      ...(isReUpload && { comment }),
    });

    const response = await dispatch(handleUploadFullPaper(payload));
    if (response.status === 200) {
      handleClose();
      dispatch(throwSuccess(response?.message));
      getAbstractFullPaper();
    }
    setBtnLoading(false);
  };

  return (
    <>
      {isReUpload ? (
        <>
          <div className="row">
            <div className="col-md-8 cmb-16">
              <div className="text-14-400-18 color-6866 mb-2">Comment *</div>
              <TextArea
                rows={3}
                placeholder="Enter your comment"
                value={comment}
                id="comment"
                onChange={(e) => {
                  setComment(e.target.value);
                  if (e.target.value.trim()) {
                    setCommentError('');
                  }
                }}
              />
              {commentError && (
                <div className="text-danger text-12 mt-1">{commentError}</div>
              )}
            </div>
          </div>

          <div className="d-flex">
            <Button
              icon={
                <span className="me-2">
                  <img src={icons.uploadIcon} alt="" height={16} width={16} />
                </span>
              }
              text="Re-Upload Full Paper"
              btnStyle="primary-dark"
              className="h-35 cps-20 cpe-20"
              onClick={() => {
                if (!comment.trim()) {
                  setCommentError('Please enter a comment');
                  return;
                }
                setShowModal(true);
              }}
            />
          </div>
        </>
      ) : (
        <Button
          icon={
            <span className="me-2">
              <img src={icons.uploadIcon} alt="" height={16} width={16} />
            </span>
          }
          text="Upload Full Paper"
          btnStyle="primary-dark"
          className="h-35 cps-20 cpe-20"
          onClick={() => setShowModal(true)}
        />
      )}

      {showModal && (
        <Modal
          titleBesideClose={
            isReUpload ? 'Re-Upload Full Paper' : 'Add Full Paper'
          }
          onHide={handleClose}
          isTitleBorder
          isTitleVerticalCenter
        >
          <Formik
            initialValues={{
              abstract_path: null,
              abstractPathFileName: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleApiSubmit(values);
            }}
          >
            {({ values, errors, touched, setFieldValue, resetForm, dirty }) => (
              <Form>
                <div className="cps-27 cpe-27 cpt-20 cpb-20">
                  <div className="text-14-400-18 color-6866 mb-2">
                    Upload Full Paper *
                  </div>
                  <div className="cmb-24">
                    <UploadInput
                      multiple={false}
                      id="abstract_path"
                      value={values.abstract_path}
                      supportedFormats={[
                        'XLS',
                        'XLSX',
                        'PDF',
                        'WORD',
                        'PPT',
                        'PPTX',
                        'DOC',
                        'DOCX',
                      ]}
                      error={touched.abstract_path && errors.abstract_path}
                      handleChange={(e) => {
                        setFieldValue('abstract_path', e?.target?.value);
                        setFieldValue(
                          'abstractPathFileName',
                          e?.target?.fileName || ''
                        );
                      }}
                    />
                    {touched.abstract_path && errors.abstract_path && (
                      <div className="text-danger text-12 mt-1">
                        {errors.abstract_path}
                      </div>
                    )}
                  </div>

                  <div className="d-flex mt-4 gap-3">
                    <Button
                      btnStyle="primary-dark"
                      text="Save"
                      className="h-37 text-14-500 text-nowrap cps-40 cpe-40"
                      type="submit"
                      btnLoading={btnLoading}
                      disabled={btnLoading || !values?.abstract_path}
                    />

                    <Button
                      btnStyle="light-outline"
                      text="Cancel"
                      className="h-37 text-14-500 text-nowrap cps-40 cpe-40"
                      type="button"
                      disabled={btnLoading}
                      onClick={() => {
                        handleClose();
                      }}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}
    </>
  );
};

export default FullPaperAddPopup;
